import React from 'react'
import { Control, Controller, FieldErrors, UseFieldArrayUpdate, UseFormGetValues, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { Checkbox } from '@mui/material';
import { useDirectoriesHook, useGeneralHooks } from '../../../../../../../Hooks/hooks';
import useOptionFormHooks from './optionForm-hook';
import { MenuItemOpionFormTypes } from '../../../../../../../Interfaces/componentTypes';
import { ICreateMenuItemForm, IMenuItemFormOption } from '../../../../../../../Interfaces/formValue-interfaces';
import AuthInput from '../../../../../../../Components/Inputs/AuthInput/authInput';
import AutoComplete from '../../../../../../../Components/Inputs/AutoComplete/autoComplete';
import { RedPlus, RedClose, Edit, Warning } from '../../../../../../../Assets/Icons';
import styles from './optionForm.module.scss';

export interface IOptionFormProps {
    type: MenuItemOpionFormTypes,
    options: IMenuItemFormOption[],
    register: UseFormRegister<ICreateMenuItemForm>,
    control: Control<ICreateMenuItemForm, any>,
    errors: FieldErrors<ICreateMenuItemForm>,
    getValues: UseFormGetValues<ICreateMenuItemForm>,
    setValue: UseFormSetValue<ICreateMenuItemForm>,
    addOption: () => void,
    update: UseFieldArrayUpdate<ICreateMenuItemForm, "option">,
    deleteOption: (index: number) => void,
}

const OptionForm: React.FC<IOptionFormProps> = (props) => {
    const { options, register, getValues, control, errors, type } = props;
    const { t } = useGeneralHooks();
    const { menuItemMeasureTypes } = useDirectoriesHook();
    const {
        addButton,
        openFormId,
        activeIndex,
        onOpenForm,
        onAddOption,
        updatePrice,
        handleCheckBox,
        onDeleteOption,
        updateMeasureType,
        updatePackageCost,
        updateMeasureValue,
    } = useOptionFormHooks(props);

    return (
        <div className={styles.container}>
            {
                options.map((option, index) => {
                    return (
                        <div key={index} className={styles.optionBox}>
                            {
                                openFormId === index
                                    ?
                                    <div className={styles.optionForm} key={index}>
                                        {
                                            index !== 0
                                            &&
                                            <div className={styles.header}>
                                                <img
                                                    src={RedClose}
                                                    alt='closeIcon.svg'
                                                    onClick={() => onDeleteOption(index)}
                                                    className={styles.closeIcon}
                                                />
                                            </div>
                                        }
                                        <div className={styles.selectBox}>
                                            <p>
                                                {t('My_Business.Create_Menu.Option_Form.Select')}
                                            </p>
                                            <Controller
                                                control={control}
                                                name={`option.${index}.measure_type`}
                                                rules={{
                                                    required: t('Errors.Required'),
                                                }}
                                                render={({ field: { onChange, name, value } }) => {
                                                    return (
                                                        <AutoComplete
                                                            value={value}
                                                            name={name}
                                                            onChange={(values) => {
                                                                updateMeasureType(index, value!)
                                                                onChange(values);
                                                            }}
                                                            id='user_id'
                                                            data={menuItemMeasureTypes}
                                                            error={errors.option?.[index]?.measure_type}
                                                            showTextError={false}
                                                        />
                                                    );
                                                }}
                                            />
                                        </div>
                                        <div className={styles.inputBox}>
                                            <p>
                                                {t('My_Business.Create_Menu.Option_Form.Value')}
                                            </p>
                                            <AuthInput
                                                register={register}
                                                registerName={`option.${index}.measure_value`}
                                                labelStyle={styles.inputLabel}
                                                type='number'
                                                onChange={(e) => {
                                                    updateMeasureValue(index, e.currentTarget.value!)
                                                }}
                                                error={errors.option?.[index]?.measure_value}
                                                showTextError={false}
                                                required={false}
                                            />
                                        </div>
                                        <div className={styles.inputBox}>
                                            <p>
                                                {t('My_Business.Create_Menu.Option_Form.Price')}
                                            </p>
                                            <AuthInput
                                                register={register}
                                                registerName={`option.${index}.price`}
                                                labelStyle={styles.inputLabel}
                                                type='number'
                                                error={errors.option?.[index]?.price}
                                                showTextError={false}
                                                onChange={(e) => {
                                                    updatePrice(index, e.currentTarget.value)
                                                }}
                                            />
                                        </div>
                                        <div className={styles.inputBox}>
                                            <p>
                                                {t('My_Business.Create_Menu.Option_Form.Package_Cost')}
                                            </p>
                                            <AuthInput
                                                register={register}
                                                registerName={`option.${index}.package_cost`}
                                                labelStyle={styles.inputLabel}
                                                type='number'
                                                error={errors.option?.[index]?.package_cost}
                                                showTextError={false}
                                                onChange={(e) => {
                                                    updatePackageCost(index, e.currentTarget.value)
                                                }}
                                                required={false}
                                            />
                                        </div>
                                        <div className={styles.checkBoxRow}>
                                            <Checkbox
                                                checked={activeIndex ? activeIndex === index : option.is_active}
                                                onChange={(e) => handleCheckBox(e, index)}
                                                sx={{
                                                    color: '#505053',
                                                    '&.Mui-checked': {
                                                    },
                                                }}
                                                size="large"
                                            />
                                            <p>{t('My_Business.Create_Menu.Option_Form.Main')}</p>
                                        </div>
                                    </div>
                                    :
                                    <div
                                        key={index}
                                        onClick={() => onOpenForm(index)}
                                        className={styles.optionItem}
                                    >
                                        <div className={styles.optionItemData}>
                                            {
                                                option.measure_type && option.price
                                                    ?
                                                    <>
                                                        <p className={styles.optionItemMesureType}>
                                                            {getValues(`option.${index}.measure_type`)?.title}
                                                        </p>
                                                        <p className={styles.optionItemMesureType}>
                                                            {getValues(`option.${index}.measure_value`)}
                                                        </p>
                                                        <p className={styles.optionItemPrice}>
                                                            {getValues(`option.${index}.price`)}
                                                        </p>
                                                        <Checkbox
                                                            checked={activeIndex ? activeIndex === index : option.is_active}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onChange={(e) => {
                                                                handleCheckBox(e, index)

                                                            }}
                                                            sx={{
                                                                color: '#505053',
                                                            }}
                                                            size="medium"
                                                        />
                                                    </>
                                                    :
                                                    <>
                                                        <img src={Warning} alt='waning.svg' />
                                                        <p className={styles.incorrectItem}>
                                                            {t('My_Business.Create_Menu.Option_Form.Incompleted')}
                                                        </p>
                                                    </>
                                            }
                                        </div>
                                        <div className={styles.optionItemData}>
                                            <img className={styles.editIcon} src={Edit} alt='edit.svg' onClick={() => onOpenForm(index)} />
                                            {
                                                index !== 0
                                                &&
                                                <img
                                                    src={RedClose}
                                                    alt='closeIcon.svg'
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        onDeleteOption(index)
                                                    }}
                                                    className={styles.closeIcon}
                                                />
                                            }
                                        </div>
                                    </div>
                            }
                        </div>
                    )
                })
            }
            {
                addButton &&
                <div className={styles.buttonRow}>
                    <div className={styles.addButton} onClick={() => onAddOption(options.length)}>
                        <img src={RedPlus} alt='plus.svg' />
                        <p>{t('My_Business.Create_Menu.Option_Form.Add')}</p>
                    </div>
                </div>
            }
        </div >
    )
}

export default OptionForm
